import React, {forwardRef, useEffect, useState} from "react";
import DesktopPopup from "./DesktopPopup";
import {LookDataType, LookItemType} from "./LookTypes";
import isSameObject from "../../../helpers/isSameObject";

type LookImageSectionPropType = {
  look: LookDataType;
  lookItems: LookItemType[] | null;
  activeProduct: LookItemType | null;
  handleClose: () => void;
  handleDotClick: (product: LookItemType, evt: React.MouseEvent<HTMLDivElement>) => Promise<void>;
  isMobile: boolean;
  isLookDetailPageItem: boolean;
};

/**
 * Main ambient image of look, including the dots and (initially invisible) `DesktopPopup`s for every individual item
 */
const LookImageSection = forwardRef<HTMLDivElement, LookImageSectionPropType>(
  ({look, lookItems, activeProduct, handleDotClick, handleClose, isMobile, isLookDetailPageItem}, imageRef) => {
    const [imageState, setImageState] = useState<{loaded: boolean; failed: boolean}>({loaded: false, failed: false});

    useEffect(() => {
      setImageState({loaded: false, failed: false});
    }, [look]);

    if (!isLookDetailPageItem && !imageState.loaded) {
      setImageState({loaded: true, failed: false});
    }

    const onImgLoad: React.ReactEventHandler<HTMLImageElement> = () => {
      setImageState({loaded: true, failed: false});
    };

    const onImgError = () => {
      setImageState({loaded: true, failed: true});
    };

    const isItemsAndImageLoaded = !!lookItems && imageState.loaded;
    const imgClassName = `main-image${!isItemsAndImageLoaded ? " main-image__tinted" : ""}`;

    return (
      <div className="image-section">
        <div className="image-container" ref={imageRef}>
          {!isItemsAndImageLoaded && <div className="swiper-lazy-preloader">{/* preloader */}</div>}
          {isLookDetailPageItem ? (
            <img
              onLoad={onImgLoad}
              onError={onImgError}
              src={look.image || undefined}
              className={imgClassName}
              alt={look.title}
            />
          ) : (
            <img src={look.image || undefined} className={imgClassName} alt={look.title} />
          )}

          {isItemsAndImageLoaded &&
            !imageState.failed &&
            lookItems.map((product, index) => {
              const isActiveProduct = isSameObject<LookItemType>(activeProduct, product);
              const {x, y} = product.coordinates;
              const coordStyles = {left: `${x}%`, top: `${y}%`};

              return (
                <div key={index}>
                  <div
                    className="dot-background"
                    style={coordStyles}
                    onClick={(evt: React.MouseEvent<HTMLDivElement>) => void handleDotClick(product, evt)}
                  />
                  <div
                    className={`product-dot${isActiveProduct ? " dot-active" : product.isAlternative ? " dot-similar" : " dot-default"}`}
                    style={coordStyles}
                  />
                  {!isMobile && <DesktopPopup product={product} isActive={isActiveProduct} handleClose={handleClose} />}
                </div>
              );
            })}
        </div>
      </div>
    );
  },
);

LookImageSection.displayName = "LookImageSection";

export default LookImageSection;
